import { useState, type FC } from "react"
import { Navigate, useParams } from "react-router"

import { parseNumber } from "@forento/shared/utilities/number"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import Button from "~/themes/original/components/Button"
import Hero, { CallToActionInfo } from "~/themes/original/components/Hero"
import Layout from "~/themes/original/components/Layout"
import PartialLoadingPage from "~/themes/original/components/PartialLoadingPage"
import PurchaseItem from "~/themes/original/components/PurchaseItem"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const BundlePage: FC = () => {
	const bundleId = parseNumber(useParams().bundleId) ?? -1

	const [paypalOrder, setPaypalOrder] = useState<{
		userStatus: "existing" | "assigned" | "created"
		email: string
	} | null>(null)

	const { data: bundle, error, refetch } = query.bundle.getPersonalized.useQuery(bundleId ?? -1)

	if (bundle === null) {
		return <Navigate to={routes.bundle.index()} />
	}

	return (
		<PurchaseSuccessModalContextProvider content={{ type: "bundle" }} paypalOrder={paypalOrder}>
			<Layout>
				{error ? (
					<p>Failed to load bundle.</p>
				) : !bundle ? (
					<PartialLoadingPage />
				) : (
					<Hero
						title={bundle.name}
						thumbnailUrl={bundle.thumbnailFilePath}
						htmlDescription={bundle.longDescription?.html ?? null}
						callToAction={
							bundle.status === "unlocked" ? (
								<Button variant="primary" isDisabled>
									You already own this bundle
								</Button>
							) : bundle.status === "paywall" ? (
								<PurchaseItem
									item={{
										type: "bundle",
										id: bundle.id,
										name: bundle.name,
										shortDescription: bundle.shortDescription,
										price: bundle.price,
									}}
									onPurchase={async ({ paypalOrder }) => {
										setPaypalOrder(paypalOrder)
										await refetch()
									}}
								/>
							) : bundle.status === "paused" ? (
								<CallToActionInfo>Sales are paused.</CallToActionInfo>
							) : (
								exhaustiveGuard(bundle.status)
							)
						}
					/>
				)}
			</Layout>
		</PurchaseSuccessModalContextProvider>
	)
}

export default BundlePage
